import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { Project, Event, Task, ProjectFile, AppState, FileCategory, User, UserDetails } from '@/types';
import { authService } from '@/services/authService';
import { userService } from '@/services/usersService';
import { projectService } from '@/services/projectService';
import { useRef } from 'react'; // להוסיף ל-import הקיים של react
import { initPushNotifications } from '@/services/PushNotifications';

interface AppContextType extends AppState {
  addProject: (project: Project) => void;
  updateProject: (id: string, updates: Partial<Project>) => void;
  addEvent: (event: Event) => void;
  addTask: (task: Task) => void;
  toggleTask: (id: string) => void;
  getProjectEvents: (projectId: string) => Event[];
  getProjectTasks: (projectId: string) => Task[];
  getProjectFiles: (projectId: string, category?: FileCategory) => ProjectFile[];
  getEventFiles: (eventId: string) => ProjectFile[];
  uploadFile: (projectId: string, file: File, category: FileCategory, eventId?: string, tags?: string[]) => Promise<void>;
  uploadFileFromBase64: (projectId: string, name: string, base64: string, type: string, category: FileCategory, eventId?: string, tags?: string[]) => void;
  getTodayTasks: () => Task[];
  getTodayEvents: () => Event[];
  login: (userData: User) => Promise<boolean>;
  logout: () => void;
  isLoggedIn: () => boolean;
  isInitialized: boolean;
  saveToLocalStorage: () => void;
  setProjectsList : (projects: Project[]) => void;
  setEventsList : (events: Event[]) => void;
  updateEvent: (id: string, updates: Partial<Event>) => void;
  setUserDetails: (userDetails: UserDetails | null) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const STORAGE_KEY = 'field-os-data';

function migrateTask(task: any): Task {
  return {
    id: task.id,
    projectId: task.projectId,
    title: task.title,
    notes: task.notes || '',
    scheduledDate: task.scheduledDate || task.dueDate || new Date().toISOString().split('T')[0],
    isCompleted: typeof task.isCompleted === 'boolean' ? task.isCompleted : task.status === 'completed'
  };
}

function loadFromStorage(): AppState | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      // Migrate tasks if needed
      if (data.tasks) {
        data.tasks = data.tasks.map(migrateTask);
      }
      return data;
    }
  } catch (e) {
    console.error('Failed to load from localStorage:', e);
  }
  return null;
}

function saveToStorage(state: AppState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error('Failed to save to localStorage:', e);
  }
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [files, setFiles] = useState<ProjectFile[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [user, setUser] = useState(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);

  useEffect(() => {
    const stored = loadFromStorage();
    if (stored){
        setProjects(stored.projects);
        setEvents(stored.events);
        setTasks(stored.tasks);
        setFiles(stored.files || []);
        setUser(stored.user || null);
        setUserDetails(stored.userDetails || null);
    }
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (isInitialized) {
      saveToStorage({ projects, events, tasks, files, userDetails });
    }
  }, [projects, events, tasks, files, userDetails, isInitialized]);

  const sessionInitRef = useRef<string | null>(null);

useEffect(() => {
  if (!isInitialized) return;
  if (!userDetails?.email) {
    sessionInitRef.current = null; // אופס בלוגאאוט
    return;
  }
  // מריצים פעם אחת לכל סשן (לא בכל render)
  if (sessionInitRef.current === userDetails.email) return;
    sessionInitRef.current = String(userDetails.email);

  // 👇 כאן הקוד שירוץ בכל כניסה של היוזר לאפליקציה
  initPushNotifications();
  // ... כל דבר אחר שתרצה
}, [isInitialized, userDetails]);

//   useEffect(() => {
//   if (!isLoggedIn()) return;

//   initPushNotifications();
// }, [isLoggedIn]);


  const addProject = (project: Project) => {
    setProjects(prev => [...prev, project]);
  };

  const updateProject = (id: string, updates: Partial<Project>) => {
    setProjects(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
  };

  const updateEvent = (id: string, updates: Partial<Event>) => {
    setEvents(prev => prev.map(e => e.id === id ? { ...e, ...updates } : e));
  }

  type LoginInput =
    | { email: string; password: string }
    | { email?: string; user: { id: string; email: string } };

  const bootstrapSession = async (email: string, userData: unknown): Promise<boolean> => {
    try {
      const projects = await projectService.getProjects();
      setProjects(projects);
      const userDataRes = await userService.getUserDetails(email);
      setUser(userData);
      setUserDetails({
        Tags: userDataRes.userTags || [],
        email: userDataRes.email,
        name: userDataRes.fullName,
        password: "",
        role: userDataRes.role,
        joinedDate: userDataRes.joinedDate,
      });
      return true;
    } catch (e) {
      console.error('Failed to bootstrap session:', e);
      return false;
    }
  };

  const login = async (userData: LoginInput): Promise<boolean> => {
    if ('user' in userData && userData.user?.id) {
      return bootstrapSession(userData.user.email, userData);
    }

    if (!('password' in userData) || !userData.email || !userData.password) {
      return false;
    }

    try {
      const res = await authService.login(userData.email, userData.password);
      if (!res?.session?.access_token) return false;

      const { access_token, refresh_token } = res.session;
      window.localStorage.setItem('fieldOsAccessToken', access_token);
      window.localStorage.setItem('access_token', access_token);
      if (refresh_token) {
        window.localStorage.setItem('refresh_token', refresh_token);
      }

      return bootstrapSession(userData.email, userData);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        logout();
      }
      return false;
    }
  };
  const logout = () => {
    if (Capacitor.isNativePlatform()) {
      SocialLogin.logout({ provider: 'google' }).catch(() => { /* stale session, ignore */ });
    }
    authService.logout(); // This will clear all tokens from localStorage
    setUserDetails(null);
    setUser(null)
  };

  const addEvent = (event: Event) => {
    setEvents(prev => [...prev, event]);
    const project = projects.find(p => p.id === event.projectId);
    if (project) {
      updateProject(event.projectId, { lastUpdate: event.date });
    }
  };

  const addTask = (task: Task) => {
    setTasks(prev => [...prev, task]);
  };

  const toggleTask = (id: string) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, isCompleted: !t.isCompleted } : t));
  };

  const getProjectEvents = (projectId: string) => {
    return events.filter(e => e.projectId === projectId).sort((a, b) =>
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  };

  const getProjectTasks = (projectId: string) => {
    return tasks.filter(t => t.projectId === projectId);
  };

  const getProjectFiles = (projectId: string, category?: FileCategory) => {
    return files.filter(f =>
      f.projectId === projectId &&
      (category ? f.category === category : true)
    ).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  };

  const getEventFiles = (eventId: string) => {
    return files.filter(f => f.eventId === eventId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  };

  const uploadFile = async (projectId: string, file: File, category: FileCategory, eventId?: string, tags?: string[]): Promise<void> => {
    if (file.size > 4096 * 1024) {
      throw new Error('הקובץ גדול מדי. הגודל המקסימלי הוא 4MB');
    }

    // Convert to Base64
    let url = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    // }

    const newFile: ProjectFile = {
      id: `f-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      projectId,
      eventId,
      category,
      name: file.name,
      url,
      type: file.type,
      createdAt: new Date().toISOString().split('T')[0],
      tags: tags || []
    };

    setFiles(prev => [...prev, newFile]);
  };

  const uploadFileFromBase64 = (projectId: string, name: string, base64: string, type: string, category: FileCategory, eventId?: string, tags?: string[]) => {
    const newFile: ProjectFile = {
      id: `f-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      projectId,
      eventId,
      category,
      name,
      url: base64,
      type,
      createdAt: new Date().toISOString().split('T')[0],
      tags: tags || []
    };

    setFiles(prev => [...prev, newFile]);
  };

  const getTodayTasks = () => {
    const today = new Date().toISOString().split('T')[0];
    return tasks.filter(t => t.scheduledDate === today);
  };


  const isLoggedIn = () => {
    return userDetails !== null;
  }

  const getTodayEvents = () => {
    const today = new Date().toISOString().split('T')[0];
    return events.filter(e => e.date === today);
  };

  const saveToLocalStorage = () => {
    saveToStorage({ projects, events, tasks, files, userDetails });
  }

  return (
    <AppContext.Provider value={{
      projects,
      events,
      tasks,
      files,
      addProject,
      updateProject,
      addEvent,
      addTask,
      toggleTask,
      getProjectEvents,
      getProjectTasks,
      getProjectFiles,
      getEventFiles,
      uploadFile,
      uploadFileFromBase64,
      getTodayTasks,
      getTodayEvents,
      user,
      login,
      logout,
      isLoggedIn,
      userDetails,
      isInitialized,
      saveToLocalStorage,
      updateEvent,
      setProjectsList: (projects: Project[]) => setProjects(projects),
      setEventsList: (events: Event[]) => setEvents(events),
      setUserDetails
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
}
